import { useMemo, useState } from "react";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { Loader2, Search, ZoomIn } from "lucide-react";

import { Input } from "@/shared/ui/input";
import { Button } from "@/shared/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/shared/ui/card";
import { Badge } from "@/shared/ui/badge";
import {
  TableCell,
  TableRow,
} from "@/shared/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/shared/ui/dialog";
import { Pagination, DataTable, MonthPicker } from "@/shared/widgets";

import {
  useReceivableSnapshotSummaries,
  useReceivableSnapshotDetail,
} from "../hooks";
import type {
  ReceivableSnapshotSummary,
} from "../utils/types";

const DEFAULT_PAGE_SIZE = 10;

const currencyFormatter = (currency?: string | null) =>
  new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: currency ?? "BRL",
    minimumFractionDigits: 2,
  });

const percentFormatter = new Intl.NumberFormat("pt-BR", {
  style: "percent",
  maximumFractionDigits: 2,
  minimumFractionDigits: 2,
});

const LIKEAPPRO_PERCENTAGE = 0.6;
const CREATORS_PERCENTAGE = 0.4;

const splitFinalAmount = (amount: number | undefined | null) => {
  const total = amount ?? 0;
  return {
    likeappro: total * LIKEAPPRO_PERCENTAGE,
    creators: total * CREATORS_PERCENTAGE,
  };
};

const formatDateTime = (value?: string | null) => {
  if (!value) return "—";
  try {
    return format(new Date(value), "dd/MM/yyyy HH:mm", { locale: ptBR });
  } catch {
    return value;
  }
};

const formatReferenceMonth = (value?: string | null) => {
  if (!value) return "—";
  const [year, month] = value.split("-");
  if (!year || !month) return value;
  // Cria data no timezone local, não UTC
  const date = new Date(Number(year), Number(month) - 1, 1);
  if (Number.isNaN(date.getTime())) return value;
  return format(date, "MMM/yyyy", { locale: ptBR });
};

// Função auxiliar para converter valores para número válido, tratando null, undefined e NaN
const toNumber = (value: number | null | undefined): number => {
  if (value == null || Number.isNaN(value)) return 0;
  return Number(value) || 0;
};

export function ReceivableSnapshotsList() {
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(DEFAULT_PAGE_SIZE);
  const [selectedSummary, setSelectedSummary] = useState<ReceivableSnapshotSummary | null>(null);
  const [paidDateMonth, setPaidDateMonth] = useState<{ month: number; year: number } | null>(null);
  const [referenceDateMonth, setReferenceDateMonth] = useState<{ month: number; year: number } | null>(null);

  // Formata o mês para o formato MM-YYYY da API
  const formatMonthForApi = (monthValue: { month: number; year: number } | null): string | undefined => {
    if (!monthValue) return undefined;
    const month = String(monthValue.month).padStart(2, "0");
    return `${month}-${monthValue.year}`;
  };

  const { summaries, totalPages, totalItems, isLoading, isFetching, error } =
    useReceivableSnapshotSummaries({
      page,
      limit,
      search: search.trim() || undefined,
      paidDate: formatMonthForApi(paidDateMonth),
      reference_date: formatMonthForApi(referenceDateMonth),
    });

  const paidDate = selectedSummary?.referenceMonth?.split("-").reverse().join("-") ?? "";

  const {
    snapshots: creatorSnapshots,
    isLoading: loadingDetail,
  } = useReceivableSnapshotDetail(paidDate, {
    
    enabled: Boolean(paidDate),
  });

  const closeDetail = () => setSelectedSummary(null);

  const metricsFormatter = useMemo(() => {
    const currency = selectedSummary?.currency || "BRL";
    return currencyFormatter(currency);
  }, [selectedSummary?.currency]);

  const creatorTotals = useMemo(
    () =>
      creatorSnapshots.reduce(
        (acc, snapshot) => ({
          final: acc.final + toNumber(snapshot.shareAmount),
          share: acc.share + toNumber(snapshot.sharePercentage),
          traffic: acc.traffic + toNumber(snapshot.trafficValue) / 100,
          averageTraffic: acc.averageTraffic + toNumber(snapshot.averageTrafficValue) / 100,
          preset: acc.preset + toNumber(snapshot.useValue) / 100,
        }),
        { final: 0, share: 0, traffic: 0, averageTraffic: 0, preset: 0 },
      ),
    [creatorSnapshots],
  );

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(event.target.value);
    setPage(1);
  };

  // Reset page when filters change
  const handlePaidDateChange = (value: { month: number; year: number } | null) => {
    setPaidDateMonth(value);
    setPage(1);
  };

  const handleReferenceDateChange = (value: { month: number; year: number } | null) => {
    setReferenceDateMonth(value);
    setPage(1);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Cálculos de Recebíveis</h1>
          <p className="text-muted-foreground">
            Acompanhe snapshots calculados no fechamento de cada recebível e explore os detalhes por creator.
          </p>
        </div>
      </div>

      {/* Filtros */}
      <Card className="bg-white/[0.03] border-white/[0.06]">
        <CardHeader>
          <CardTitle>Filtros</CardTitle>
          <CardDescription>Busque por ID do recebível, mês de pagamento ou mês de referência</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col gap-4 md:flex-row md:items-center">
            <div className="relative md:w-80">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Buscar por ID do recebível..."
                value={search}
                onChange={handleSearchChange}
                className="pl-9"
              />
            </div>
            <div className="w-full md:w-[280px]">
              <MonthPicker
                value={paidDateMonth}
                onChange={handlePaidDateChange}
                placeholder="Mês de pagamento"
              />
            </div>
            <div className="w-full md:w-[280px]">
              <MonthPicker
                value={referenceDateMonth}
                onChange={handleReferenceDateChange}
                placeholder="Mês de referência"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Tabela */}
      <DataTable
        data={summaries}
        isLoading={isLoading}
        error={error}
        emptyTitle="Nenhum cálculo encontrado"
        emptyDescription={(hasFilters) =>
          hasFilters
            ? "Tente ajustar os filtros"
            : "Nenhum snapshot foi calculado ainda"
        }
        hasFilters={Boolean(search || paidDateMonth || referenceDateMonth)}
        errorMessage={`Erro ao carregar cálculos: ${error?.message}`}
        rowKey={(summary) => summary.receivableId}
        columns={[
          {
            header: "Mês referência",
            cell: (summary) => summary.referenceMonth.split("-").reverse().join("/"),
          },
          {
            header: "Status",
            cell: (summary) => (
                          <Badge variant={summary.status === "CONCLUDED" ? "default" : "secondary"}>
                            {summary.status ?? "—"}
                          </Badge>
            ),
          },
          {
            header: "Creators",
            cell: (summary) => summary.creatorCount,
          },
          {
            header: "Bruto",
            cell: (summary) => {
              const formatter = currencyFormatter(summary.currency);
              return formatter.format(summary.grossAmount);
            },
          },
          {
            header: "Líquido",
            cell: (summary) => {
              const formatter = currencyFormatter(summary.currency);
              return formatter.format(summary.netAmount);
            },
          },
          {
            header: "Valor Creators (40%)",
            cell: (summary) => {
              const formatter = currencyFormatter(summary.currency);
              return formatter.format(summary.shareAmount);
            },
          },
          {
            header: "Calculado em",
            cell: (summary) => formatDateTime(summary.calculatedAt),
          },
          {
            header: "Detalhes",
            cell: (summary) => (
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => setSelectedSummary(summary)}
                          >
                            <ZoomIn className="mr-2 h-4 w-4" />
                            Abrir
                          </Button>
            ),
            className: "text-right",
          },
        ]}
      />

      {/* Pagination */}
      {!isLoading && !error && totalPages > 0 && (
          <Pagination
            currentPage={page}
            totalPages={Math.max(totalPages, 1)}
            totalItems={totalItems}
          setCurrentPage={setPage}
            pageSize={limit}
          showPageSizeOptions
          pageSizeOptions={[10, 20, 50]}
            onPageSizeChange={(value) => {
              setLimit(value);
              setPage(1);
            }}
          showTotalItems
          syncWithQueryParams={false}
          />
      )}

      <Dialog open={Boolean(selectedSummary)} onOpenChange={(open) => !open && closeDetail()}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto space-y-5">
          <DialogHeader>
            <DialogTitle>Detalhes do recebível</DialogTitle>
            <DialogDescription>
              Visualize os valores calculados para cada creator deste recebível.
            </DialogDescription>
          </DialogHeader>

          {selectedSummary && (
            <div className="space-y-4">
              <div className="grid gap-3 rounded-lg border border-white/10 bg-muted/10 p-4">
                <div>
                  <p className="text-xs uppercase tracking-wide text-muted-foreground">Mês de recebimento</p>
                  <p className="text-sm font-semibold">{selectedSummary.referenceMonth}</p>
                </div>
              </div>

              <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
                <MetricCard label="Valor bruto" value={metricsFormatter.format(selectedSummary.grossAmount)} />
                <MetricCard label="Valor líquido" value={metricsFormatter.format(selectedSummary.netAmount)} />
                <MetricCard
                  label="Valor Creators (40%)"
                  value={metricsFormatter.format(selectedSummary.shareAmount)}
                />
              </div>
            </div>
          )}

          <div className="rounded-lg border border-white/10">
            <div className="flex flex-col gap-2 border-b border-white/10 px-4 py-3 md:flex-row md:items-center md:justify-between">
              <div>
                <p className="text-sm font-semibold">Distribuição entre creators</p>
                <p className="text-xs text-muted-foreground">
                  Valor final de cada creator e os componentes percentuais utilizados no cálculo.
                </p>
              </div>
              <div className="flex flex-wrap items-center gap-3">
                <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  Total creators:
                </span>
                <span className="rounded-md bg-primary/10 px-3 py-1 text-sm font-semibold text-primary">
                  {metricsFormatter.format(creatorTotals.final)}
                </span>
                {loadingDetail && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
              </div>
            </div>
            <div className="overflow-x-auto">
              <DataTable
                data={creatorSnapshots}
                isLoading={loadingDetail}
                emptyTitle="Nenhum snapshot encontrado"
                emptyDescription={() => "Nenhum snapshot encontrado."}
                noCard
                columns={[
                  {
                    header: "Creator",
                    cell: (snapshot) => (
                      <div className="flex flex-col gap-0.5 font-mono text-xs">
                            <span className="font-semibold text-foreground">
                              {snapshot.creatorName || snapshot.creatorId}
                            </span>
                            <span className="text-muted-foreground">{snapshot.creatorId}</span>
                          </div>
                    ),
                    className: "font-mono text-xs",
                  },
                  {
                    header: "% Share",
                    cell: (snapshot) => percentFormatter.format(toNumber(snapshot.sharePercentage) / 100),
                  },
                  {
                    header: "Total (Creators)",
                    cell: (snapshot) => metricsFormatter.format(toNumber(snapshot.shareAmount)),
                  },
                  {
                    header: "Tráfego (%)",
                    cell: (snapshot) => percentFormatter.format(toNumber(snapshot.trafficValue)),
                  },
                  {
                    header: "Média tráfego (clicks)",
                    cell: (snapshot) => percentFormatter.format(toNumber(snapshot.averageTrafficValue)),
                  },
                  {
                    header: "Uso de preset",
                    cell: (snapshot) => percentFormatter.format(toNumber(snapshot.useValue) / 100),
                  },
                ]}
                footer={
                  <TableRow className="bg-muted/20 font-semibold">
                    <TableCell>Totais</TableCell>
                    <TableCell>{percentFormatter.format(toNumber(creatorTotals.share) / 100)}</TableCell>
                    <TableCell>{metricsFormatter.format(toNumber(creatorTotals.final))}</TableCell>
                    <TableCell>{percentFormatter.format(toNumber(creatorTotals.traffic))}</TableCell>
                    <TableCell>{percentFormatter.format(toNumber(creatorTotals.averageTraffic))}</TableCell>
                    <TableCell>{percentFormatter.format(toNumber(creatorTotals.preset))}</TableCell>
                  </TableRow>
                }
              />
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function MetricCard({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-lg border border-white/10 bg-muted/10 p-4">
      <p className="text-xs uppercase tracking-wide text-muted-foreground">{label}</p>
      <p className="text-lg font-semibold">{value}</p>
    </div>
  );
}
